import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useGetTablePreferenceQuery, useUpsertTablePreferenceMutation, useResetTablePreferenceMutation } from '../api';

/**
 * useTablePreferences
 * Liste tablolari icin kullaniciya ozel, BE'de kalici kolon SIRALAMA + GIZLEME tercihleri.
 *
 * - BE kaynak dogruluk: GET /GetByTableKey ile tercih cekilir.
 * - localStorage cache (`tablePrefs:{tableKey}`) ile anlik uygulama; BE gelince senkronlanir.
 * - Sema dayanikliligi: kayitli sirada olmayan yeni kolon sona eklenir, artik olmayan field yok sayilir.
 * - `lockedFromPrefs` kolonlar gizlenemez/tasinmaz; `lockedPosition: 'start' | 'end'` (vars. 'end').
 * - Degisiklikler ~500ms debounce ile Upsert'lenir.
 *
 * @param {string} tableKey  Global benzersiz tablo anahtari
 * @param {Array}  columns   Kolon config dizisi ({ field, header, lockedFromPrefs, lockedPosition, ... })
 */
const DEBOUNCE_MS = 500;
const cacheKey = (tableKey) => `tablePrefs:${tableKey}`;

const readCache = (tableKey) => {
    if (!tableKey) return null;
    try {
        const raw = localStorage.getItem(cacheKey(tableKey));
        if (!raw) return null;
        const parsed = JSON.parse(raw);
        return {
            columnOrder: Array.isArray(parsed?.columnOrder) ? parsed.columnOrder : [],
            hiddenColumns: Array.isArray(parsed?.hiddenColumns) ? parsed.hiddenColumns : []
        };
    } catch {
        return null;
    }
};

const writeCache = (tableKey, prefs) => {
    if (!tableKey) return;
    try {
        localStorage.setItem(cacheKey(tableKey), JSON.stringify(prefs));
    } catch {
        /* sessizce gec (kota/private mode) */
    }
};

const clearCache = (tableKey) => {
    if (!tableKey) return;
    try {
        localStorage.removeItem(cacheKey(tableKey));
    } catch {
        /* yok say */
    }
};

const EMPTY_PREFS = { columnOrder: [], hiddenColumns: [] };

export default function useTablePreferences(tableKey, columns) {
    const safeColumns = useMemo(() => (Array.isArray(columns) ? columns.filter((c) => c && c.field) : []), [columns]);

    const { data: bePref, isFetching } = useGetTablePreferenceQuery(tableKey, { skip: !tableKey });
    const [upsertTablePreference] = useUpsertTablePreferenceMutation();
    const [resetTablePreference] = useResetTablePreferenceMutation();

    const [localPrefs, setLocalPrefs] = useState(() => readCache(tableKey) ?? EMPTY_PREFS);
    const debounceRef = useRef(null);

    // BE cevabi gelince senkronla (BE kazanir) ve cache'i guncelle.
    useEffect(() => {
        if (bePref === undefined) return; // hala yukleniyor
        const next = bePref
            ? {
                  columnOrder: Array.isArray(bePref.columnOrder) ? bePref.columnOrder : [],
                  hiddenColumns: Array.isArray(bePref.hiddenColumns) ? bePref.hiddenColumns : []
              }
            : EMPTY_PREFS;
        setLocalPrefs(next);
        writeCache(tableKey, next);
    }, [bePref, tableKey]);

    useEffect(() => () => clearTimeout(debounceRef.current), []);

    // Tercih sistemine dahil (locked olmayan) kolonlar
    const prefManaged = useMemo(() => safeColumns.filter((c) => !c.lockedFromPrefs), [safeColumns]);
    const prefManagedFields = useMemo(() => prefManaged.map((c) => c.field), [prefManaged]);

    const lockedStart = useMemo(() => safeColumns.filter((c) => c.lockedFromPrefs && (c.lockedPosition ?? 'end') === 'start'), [safeColumns]);
    const lockedEnd = useMemo(() => safeColumns.filter((c) => c.lockedFromPrefs && (c.lockedPosition ?? 'end') === 'end'), [safeColumns]);

    // Etkin tam sira (locked haric, gorunur + gizli): kayitli sira + yeni kolonlar sona
    const resolvedOrder = useMemo(() => {
        const saved = (localPrefs.columnOrder || []).filter((f) => prefManagedFields.includes(f));
        const appended = prefManagedFields.filter((f) => !saved.includes(f));
        return [...saved, ...appended];
    }, [localPrefs.columnOrder, prefManagedFields]);

    const hiddenColumns = useMemo(() => (localPrefs.hiddenColumns || []).filter((f) => prefManagedFields.includes(f)), [localPrefs.hiddenColumns, prefManagedFields]);

    const colByField = useMemo(() => {
        const map = new Map();
        prefManaged.forEach((c) => map.set(c.field, c));
        return map;
    }, [prefManaged]);

    // Ayar paneli icin: tam sira (gizli dahil) config objeleri
    const allColumns = useMemo(() => resolvedOrder.map((f) => colByField.get(f)).filter(Boolean), [resolvedOrder, colByField]);

    // Render edilecek kolonlar: lockedStart + (gorunur+sirali) + lockedEnd
    const renderColumns = useMemo(() => {
        const visibleOrdered = resolvedOrder.filter((f) => !hiddenColumns.includes(f)).map((f) => colByField.get(f)).filter(Boolean);
        return [...lockedStart, ...visibleOrdered, ...lockedEnd];
    }, [resolvedOrder, hiddenColumns, colByField, lockedStart, lockedEnd]);

    const applyPrefs = useCallback(
        (next) => {
            setLocalPrefs(next);
            writeCache(tableKey, next);
            clearTimeout(debounceRef.current);
            debounceRef.current = setTimeout(() => {
                if (!tableKey) return;
                upsertTablePreference({ tableKey, columnOrder: next.columnOrder, hiddenColumns: next.hiddenColumns });
            }, DEBOUNCE_MS);
        },
        [tableKey, upsertTablePreference]
    );

    const setHidden = useCallback(
        (fields) => {
            const nextHidden = (Array.isArray(fields) ? fields : []).filter((f) => prefManagedFields.includes(f));
            // En az 1 gorunur (pref) kolon zorunlu
            if (prefManagedFields.length > 0 && nextHidden.length >= prefManagedFields.length) return;
            applyPrefs({ columnOrder: resolvedOrder, hiddenColumns: nextHidden });
        },
        [applyPrefs, prefManagedFields, resolvedOrder]
    );

    const toggleHidden = useCallback(
        (field) => {
            if (!prefManagedFields.includes(field)) return;
            const isHidden = hiddenColumns.includes(field);
            const nextHidden = isHidden ? hiddenColumns.filter((f) => f !== field) : [...hiddenColumns, field];
            setHidden(nextHidden);
        },
        [hiddenColumns, prefManagedFields, setHidden]
    );

    const reorder = useCallback(
        (orderedFields) => {
            const incoming = (Array.isArray(orderedFields) ? orderedFields : []).filter((f) => prefManagedFields.includes(f));
            let nextOrder;
            if (incoming.length === prefManagedFields.length) {
                // Tam liste (ayar paneli / OrderList) -> dogrudan kullan
                nextOrder = incoming;
            } else {
                // Sadece gorunur kolonlar (baslik suruklemesi) -> gizli kolonlari yerinde koruyarak birlestir
                let vi = 0;
                nextOrder = resolvedOrder.map((f) => {
                    if (hiddenColumns.includes(f)) return f;
                    const replacement = incoming[vi];
                    vi += 1;
                    return replacement ?? f;
                });
            }
            applyPrefs({ columnOrder: nextOrder, hiddenColumns });
        },
        [applyPrefs, prefManagedFields, resolvedOrder, hiddenColumns]
    );

    const reset = useCallback(() => {
        clearTimeout(debounceRef.current);
        setLocalPrefs(EMPTY_PREFS);
        clearCache(tableKey);
        if (tableKey) resetTablePreference(tableKey);
    }, [tableKey, resetTablePreference]);

    return {
        columns: renderColumns,
        allColumns,
        hiddenColumns,
        setHidden,
        toggleHidden,
        reorder,
        reset,
        isLoading: isFetching
    };
}
